import styles from '../../uiEditor/uiEditor.module.scss';
import { useMemo, FC, useRef, useCallback } from 'react';
import { Trans } from 'react-i18next';
import { useModal } from '../../../../application/hooks/useModal';
import { useScopedTranslation } from '../../../../application/localisation/useScopedTranslation';
import { useCommonLocalization } from '../../../../application/localisation/useCommonLocalization';
import { Deferred } from '../../../../helpers/Deferred';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useContextSelector } from 'use-context-selector';
import { FormFillContext } from '../../uiEditor/provider/formFill.context';
import { layoutToDataKeyFlatList } from '../../uiEditor/uiEditor-helper';
import { SerializedNode } from '@craftjs/core';
import { displayFieldResolver } from './displayFieldResolver';

export const useNotEditableFieldModal = () => {
  const { t } = useScopedTranslation('Forms');
  const commonLocalizer = useCommonLocalization();
  const modal = useModal('CLOSED');
  const deferred = useRef<Deferred>();

  return useMemo(
    () => ({
      open: () => {
        deferred.current = new Deferred();
        modal.openModal();
        return deferred.current.promise;
      },
      element: (
        <DialogModal
          title={t('PatientDataModal.Header')}
          testId={'not-editable-fields-modal'}
          visible={modal.visible}
          bodyClassName={styles.modalBody}
          onHide={modal.closeModal}
          footer={{
            leftButton: {
              text: commonLocalizer('Common_Back'),
              onClick: modal.closeModal,
            },
            rightButton: {
              text: t('Controls.SaveButton'),
              onClick: () => {
                modal.closeModal();
                deferred.current?.resolve();
              },
            },
          }}
        >
          <ModalContentComponent />
        </DialogModal>
      ),
    }),
    [commonLocalizer, modal, t],
  );
};

const ModalContentComponent: FC = () => {
  const { notEditableNodes, nodes } = useContextSelector(FormFillContext, (x) => x);
  const dataKeys = useMemo(() => (nodes ? layoutToDataKeyFlatList(nodes) : []), [nodes]);
  const orderByScheme = useCallback(
    (a: SerializedNode, b: SerializedNode) => dataKeys.indexOf(a.props.dataKey) - dataKeys.indexOf(b.props.dataKey),
    [dataKeys],
  );

  const renderNotEditableFields = useMemo(() => {
    const result = notEditableNodes?.sort(orderByScheme).map((node) => {
      // @ts-ignore
      const ResolvedComponent = displayFieldResolver[node.type.resolvedName];
      return (
        <p key={node.props.dataKey}>
          <span>{node.props.label ?? node.props.text ?? node.props.dataKey}</span>
          <ResolvedComponent {...node?.props} />
        </p>
      );
    });

    return <div className={styles.patientDataContainer}>{result}</div>;
  }, [notEditableNodes, orderByScheme]);

  return (
    <>
      <div className={styles.mainText}>
        <Trans i18nKey={'Forms.PatientDataModal.MainText'}>
          <span className={styles.awfulText} />
        </Trans>
      </div>
      {renderNotEditableFields}
    </>
  );
};
